import React, {useCallback, useEffect, useMemo, useState} from "react";
import { useSelector } from "react-redux";
import Button from "../../../../common/components/Button";
import PlayersInGame from "../../../../common/components/PlayersInGame";
import {IConductedStoryGameConfig, IPlayer, LocalStorage, PlayerState, TimeRange} from "../../../../common/types";
import { getPlayers } from "../../../../redux/playersSlice";
import T from "../../../../common/i18n/Text";
import PlayScreen from "../../../../common/components/game-screens/PlayScreen";
import { getCurrentLang } from "../../../../common/utils/lang";
import {getNextRandomLocalisedAndSaveAsUsed} from "../../../../common/utils/storage";

const topicsData = require("../topics.json");

interface Props {
    backToDetails: () => void;
    backToConfig: () => void;
    config: IConductedStoryGameConfig;
}

const LONG_TR = new TimeRange(6, 10);
const MEDIUM_TR = new TimeRange(4, 8);
const SHORT_TR = new TimeRange(2, 6);

const ConductedStoryGamePlay = ({ backToDetails, backToConfig, config }: Props) => {
    const [gameHasAlreadyStarted, markGameAsStarted] = useState(false);
    const [timeRange, setTimeRange] = useState(LONG_TR);
    const [isIntervalConstRun, setIsIntervalConstRun] = useState(false);

    const activeLang = getCurrentLang();

    const players = useSelector(getPlayers);
    const playersInGame = useMemo(() => {
        return players.filter(player => config.selectedPlayerIds.includes(player.id));
    }, [config.selectedPlayerIds, players]);

    const [activePlayer, setActivePlayer] = useState<IPlayer | null>(null);

    const playersWithState = useMemo(() => {
        return playersInGame
            .map(player => ({
                ...player,
                state: (activePlayer?.id === player.id ? PlayerState.ACTIVE : PlayerState.DEFAULT)
            }));
    }, [playersInGame, activePlayer]);

    const getNextActivePlayer = useCallback((): IPlayer => {
        const playersToSelectFrom = activePlayer === null ? playersInGame.length : playersInGame.length - 1;
        const randomIndex = Math.floor(Math.random() * playersToSelectFrom);
        return playersInGame.filter(player => player.id !== activePlayer?.id)[randomIndex];
    }, [activePlayer, playersInGame]);

    const INTERVAL = 15000;

    // changing time range from long to medium then to short each 15 sec
    useEffect(() => {
        if (!isIntervalConstRun) return;
        const timeout = setTimeout(() => {
            timeRange === LONG_TR
                ? setTimeRange(MEDIUM_TR) :
                (MEDIUM_TR
                    ? setTimeRange(SHORT_TR)
                    : setTimeRange(LONG_TR));
        }, INTERVAL);

        return () => clearTimeout(timeout);
    }, [timeRange, isIntervalConstRun]);

    // changing active player
    useEffect(() => {
        if (!isIntervalConstRun) return;

        const timeout = setTimeout(setActivePlayer, timeRange.getRandomInterval(), getNextActivePlayer());
        return () => clearTimeout(timeout);
    }, [playersInGame, timeRange, isIntervalConstRun, activePlayer, getNextActivePlayer]);

    const onResumeClick = () => {
        setIsIntervalConstRun(true);
        markGameAsStarted(true);
        setTimeRange(LONG_TR);
        setActivePlayer(getNextActivePlayer());
    };

    const onPauseClick = () => {
        setIsIntervalConstRun(false);
        setActivePlayer(null)
    };

    const buttonPause = <Button type="secondary" onClick={onPauseClick} name="conducted-story-pause">
                            <T path='games.buttons.pause' />
                        </Button>;

    const buttonResume = <Button type="primary" onClick={onResumeClick} name="conducted-story-resume">
                            <T path={`games.buttons.${gameHasAlreadyStarted ? 'resume' : 'play'}`} />
                        </Button>;

    const button = isIntervalConstRun ? buttonPause : buttonResume;

    const theme = useMemo(() => {
        return config.randomTopic
            ? getNextRandomLocalisedAndSaveAsUsed(topicsData, LocalStorage.USED_CONDUCTED_STORY_TOPICS_STORAGE)
            : {ru: config.customTopic,  en: config.customTopic};
    }, [config.randomTopic, config.customTopic]);

    return (
        <PlayScreen mainActions={button} showConfig={backToConfig} showInfo={backToDetails}>
            <div className="h-full grid grid-rows-[auto_1fr]">
                <h2 className="theme text-[#252429] text-[36px] text-center leading-[44px] font-bold mb-[40px]">
                    {theme[activeLang]}
                </h2>
                <PlayersInGame playersInGame={playersWithState}/>
            </div>
        </PlayScreen>
    )
};

export default ConductedStoryGamePlay;
